<template lang="pug">
  .manageDevices
    //- Barra de navegación
    van-nav-bar(
      :title="$t('addUnit.unit.info')"
      left-arrow
      @click-left="$emit('close')"
      @click-right="saveDevice")
      van-icon(name="save" slot="right")

    .main.padding-double-bottom
      van-cell-group

        //- Name input
        van-field(
          required
          border=false
          input-align="right"
          name="name"
          v-model="name"
          v-validate="{ required: true, max:15, min:1, regex: nameRegex }"
          :data-vv-as="$t('addUnit.unit.name')"
          :label="$t('addUnit.unit.name')"
          :class="{'error': errors.has('name') }"
          :placeholder="$t('addUnit.unit.name')")

        //- Installation input
        van-cell(
          required
          :is-link="!device"
          :disabled="!device"
          :title="$t('addUnit.unit.group')"
          :value="installationName"
          @click="showSelectInstallation")

        //- Icon input
        van-cell(
          is-link
          required
          :title="$t('addUnit.unit.setIcon')"
          @click="showManageIcons")
          van-icon.van-cell__right-icon.units__cell__icon(
            slot="right-icon"
            :name="iconName")

        //- Show leds
        van-switch-cell(
          v-if="showLedsInfo"
          v-model="ledsOn"
          :title="$t('unit.ledsOff')")

      //- Errores de validación del formulario
      .padding-halve-top.padding-halve-bottom
        .auth__error(v-show="errors.has('name')") {{ errors.first('name') }}
        .auth__error(v-show="installationNotSelected") {{ $t('manageUnit.installationNotSelected') }}

    //- Botones
    .button-bottom-group(v-if="device")
      .button-bottom.text-danger(@click="confirmDeleteDevice") {{ $t('addUnit.delete') }}

    //- Popup Select Icon
    van-popup.van-popup--full.bg-white(
      v-model="openManageIcons"
      position="right"
      :overlay="false")

      ManageIcons(
        v-if="openManageIcons"
        :acsAvailable="acsAvailable"
        :icon="icon"
        @setIcon="setIcon"
        @close="hideManageIcons")

    //- Popup Select Installation
    van-popup.van-popup--full(
      v-model="openSelectInstallation"
      position="right"
      :overlay="false")

      SelectInstallation(
        v-if="openSelectInstallation"
        :installationID="installationSelected"
        @setInstallation="setInstallation"
        @close="hideSelectInstallation")
</template>

<script>
import i18n from '@/locales/i18n';
import { getErrorId } from '@/services/error.service';
import InstallationService from '@/services/installation.service';
import SocketService from '@/services/socket.service';
import ManageIcons from '@/components/Units/ManageIcons.vue';
import SelectInstallation from '@/components/Units/SelectInstallation.vue';
import GetDeviceIcon from '@/mixins/GetDeviceIcon.mixin';
import { hasProp } from '@/utils/validate.utils';
import { REGEX } from '@/app.config';
import { mapGetters } from 'vuex';

export default {
  components: { ManageIcons, SelectInstallation },
  mixins: [GetDeviceIcon],

  data() {
    return {
      device: null,
      name: '',
      icon: null,
      iconName: '',
      ledsOn: false,
      installationSelected: '',
      installationName: '',
      isLoading: false,
      deviceError: null,
      installationNotSelected: false,
      installationError: false,
      nameRegex: REGEX.DEVICES_NAMES,
      openManageIcons: false,
      openSelectInstallation: false,
    };
  },

  props: {
    /**
     * Mac del dispositivo
     */
    mac: {
      type: String,
      default: null,
    },
    /**
     * ID de la instalación a la que pertenece el dispositivo
     */
    installationID: {
      type: String,
      default: null,
    },
    /**
     * Indica si es una unidad altherma
     */
    altherma: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    ...mapGetters({
      getInstallation: 'unitsModule/getInstallation',
      getDevice: 'unitsModule/getDevice',
    }),

    acsAvailable() {
      if (this.device) return this.device.acs_available;
      return this.altherma;
    },

    showLedsInfo() {
      if (this.device) return hasProp(this.device, 'leds_off');
      return false;
    },
  },

  methods: {
    /**
     * Obtiene los datos de un dispositivo almacenados en el Store
     */
    getDeviceData(installationID, mac) {
      const installation = this.getInstallation(installationID);
      this.device = this.getDevice(installationID, mac);
      this.name = this.device.name;
      this.icon = this.device.icon;
      this.iconName = this.GetDeviceIcon(this.device);
      this.installationName = installation.name;
      this.ledsOn = !this.device.leds_off;
    },

    /**
     * Salva una dispositivo
     *
     * Comprueba si los datos del formulario son válidos
     * Comprueba si se trata de una dispositivo nuevo o de una edición
     */
    saveDevice() {
      this.$validator.validateAll().then(result => {
        if (result) {
          if (this.installationID) {
            this.editDevice();
          } else if (this.installationSelected) {
            this.addDevice();
          } else {
            this.installationNotSelected = true;
          }
        }
      });
    },

    /**
     * Añade un nuevo dispositivo a una instalación
     */
    async addDevice() {
      try {
        // Compruebo si hay una petición en curso,
        // si la hay, no hago nada
        if (this.isLoading) return;

        // Muestro el loader
        this.$toast.loading({
          duration: 0,
          mask: true,
          message: i18n.t('general.loading'),
        });

        // Inicializo valores
        this.isLoading = true;
        this.deviceError = null;

        console.log('Installation selected', this.installationSelected);
        // Obtengo los datos
        const device = {
          installation_id: this.installationSelected,
          mac: this.mac,
          icon: this.icon,
          name: this.name,
        };

        // Realizo la petición
        await InstallationService.saveDevice(device);

        this.$toast.clear();

        // Cierro el componente y devuelvo los datos de la instalación creada
        this.$toast.success(i18n.t('manageUnit.addUnitSuccess'));
        this.$emit('successAssociate', device);
      } catch (error) {
        // Obtengo el error
        this.deviceError = getErrorId(error);

        this.$toast.clear();

        // Indico que ocurrió un error y cierro el componente
        switch (this.deviceError) {
          case 'deviceAssociated':
            this.$dialog
              .alert({
                title: i18n.t('addUnit.unit.deviceAssociated.title'),
                message: i18n.t('addUnit.unit.deviceAssociated.message'),
              })
              .then(() => {
                this.$emit('close');
              });
            break;
          default:
            this.$dialog
              .alert({
                title: i18n.t('addUnit.unit.fails.title'),
                message: i18n.t('addUnit.unit.fails.message'),
              })
              .then(() => {
                this.$emit('close');
              });
            break;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Edita los datos de una instalación
     */
    async editDevice() {
      try {
        // Compruebo si hay una petición en curso,
        // si la hay, no hago nada
        if (this.isLoading) return;

        // Muestro el loader
        this.$toast.loading({
          duration: 0,
          mask: true,
          message: i18n.t('general.loading'),
        });

        // Inicializo valores
        this.isLoading = true;
        this.deviceError = null;

        // Obtengo los datos
        const device = {
          installation_id: this.installationID,
          mac: this.mac,
          icon: this.icon,
          name: this.name,
        };

        // Realizo la petición
        await InstallationService.updateDevice(device);

        // Si se pueden configurar los leds, también los actualizo
        if (this.showLedsInfo) {
          const deviceLed = {
            mac: this.mac,
            property: 'leds_off',
            value: !this.ledsOn,
          };

          SocketService.setDeviceValue(this.installationID, deviceLed);
        }

        this.$toast.clear();
        this.$emit('editDevice');
      } catch (error) {
        // Obtengo el error
        this.deviceError = getErrorId(error);

        this.$toast.clear();

        // Indico que ocurrió un error y cierro el componente
        switch (this.deviceError) {
          case 'deviceAssociated':
            this.$dialog
              .alert({
                title: i18n.t('addUnit.unit.deviceAssociated.title'),
                message: i18n.t('addUnit.unit.deviceAssociated.message'),
              })
              .then(() => {
                this.$emit('close');
              });
            break;
          default:
            this.$toast.fail({ mask: true, message: i18n.t('addUnit.unit.fails.message') });
            break;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Confirma si realmente quiere eliminar el dispositivo
     */
    confirmDeleteDevice() {
      this.$dialog
        .confirm({
          title: i18n.t('addUnit.unit.deleteConfirm.title'),
          message: i18n.t('addUnit.unit.deleteConfirm.message'),
        })
        .then(() => {
          this.deleteDevice();
        });
    },

    /**
     * Elimina una instalación
     */
    async deleteDevice() {
      try {
        // Compruebo si hay una petición en curso,
        // si la hay, no hago nada
        if (this.isLoading) return;

        // Muestro el loader
        this.$toast.loading({
          duration: 0,
          mask: true,
          message: i18n.t('general.loading'),
        });

        // Inicializo valores
        this.isLoading = true;
        this.installationError = null;

        const device = {
          installation_id: this.installationID,
          mac: this.mac,
        };

        // Realizo la petición
        await InstallationService.deleteDevice(device);

        this.$toast.clear();
        this.$emit('deleteDevice');
      } catch (error) {
        // Obtengo el error
        this.installationError = getErrorId(error);

        this.$toast.clear();

        // Indico que ocurrió un error y cierro el componente
        switch (this.deviceError) {
          case 'deviceAssociated':
            this.$dialog
              .alert({
                title: i18n.t('addUnit.unit.deviceAssociated.title'),
                message: i18n.t('addUnit.unit.deviceAssociated.message'),
              })
              .then(() => {
                this.$emit('close');
              });
            break;
          default:
            this.$toast.fail({ mask: true, message: i18n.t('addUnit.unit.fails.message') });
            break;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Muestra el modal para seleccionar el icono
     */
    showManageIcons() {
      this.openManageIcons = true;
    },

    /**
     * Oculta el modal para seleccionar el icono
     */
    hideManageIcons() {
      this.openManageIcons = false;
    },

    /**
     * Guarda el icono seleccionado y cierra el modal
     */
    setIcon(icon) {
      const device = {
        acs_available: this.acsAvailable,
        icon,
      };
      this.icon = icon;
      this.iconName = this.GetDeviceIcon(device);
      this.hideManageIcons();
    },

    /**
     * Muestra el modal para seleccionar la instalación
     */
    showSelectInstallation() {
      if (this.installationID) return;
      this.openSelectInstallation = true;
    },

    /**
     * Oculta el modal para seleccionar la instalación
     */
    hideSelectInstallation() {
      this.openSelectInstallation = false;
    },

    /**
     * Guarda la instalación seleccionada y cierra el modal
     */
    setInstallation(data) {
      this.installationSelected = data.installationID;
      this.installationName = data.name;
      this.hideSelectInstallation();
      this.installationNotSelected = !data.installationID;
    },
  },

  async created() {
    /**
     * Compruebo si se trata de un dispositivo nuevo o de editarlo
     */
    this.installationSelected = this.installationID;

    console.log('MANAGE DEVICE =====>', this.installationID, this.installationSelected);

    if (this.installationID && this.mac) {
      this.getDeviceData(this.installationID, this.mac);
    } else {
      this.setIcon(1);
    }
  },
};
</script>
